// ============================================
// PARTICIPANT TYPES
// ============================================

export interface BillingConfig {
    generate_billets: boolean | null;
    automatic_preventive_collection: boolean | null;
    automatic_overdue_collection: boolean | null;
}

export interface Participant {
    id: string;
    person_id: string;
    participant_type: 'BORROWER' | 'GUARANTOR' | 'ASSIGNOR' | 'GUARANTOR-RETAIL' | 'RECIPIENT' | 'BUSINESS_PARTNER';
    name: string;
    document: string;
}

export interface GuarantorItem {
    uuid: string;
    name: string;
    document: string;
    entity_type: string;
    id?: string;
    person_id?: string;
    is_active?: boolean;
    is_primary?: boolean;
    effective_to?: string | null;
    effective_from?: string;
    guarantee_value?: number | null;
    participant_type_id?: number;
    participation_order?: number;
    guarantee_percentage?: number | null;
    participant_type_name?: string;
    receives_disbursement?: boolean;
}

export interface GuarantorsData {
    items: GuarantorItem[];
    quantity: number;
}

// ============================================
// RECEIVABLE TYPES
// ============================================

export interface ReceivableItem {
    person_id?: string;
    receivable_id?: string;
    cfop: string;
    issue_date: string;
    debtor_name: string;
    document_id: string;
    address_city: string;
    debtor_email: string;
    address_phone: string;
    address_state: string;
    assignor_cnpj: string;
    assignor_name: string;
    address_number: string;
    address_street: string;
    document_title: string;
    xml_size_bytes: number;
    address_country: string;
    debtor_cpf_cnpj: string;
    address_district: string;
    address_zip_code: string;
    receivable_v_pag: string;
    address_ibge_code: string;
    receivable_amount: number;
    receivable_nat_op: string;
    receivable_number: string;
    address_complement?: string;
    receivable_ind_pag: string;
    receivable_due_date: string;
    disbursement_value: number;
    fee_name: string;
    fee_value: number;
    guarantee_percent: number;
    main_risk: string;
    external_link: string;
    operation_date?: string;
    present_value?: number;
    has_value_divergence?: boolean;
    spreadsheet_nf_value?: number;
}

export const hasReceivablesWithEmptyDueDate = (items: ReceivableItem[]): boolean => {
    return items.some(item => !item.receivable_due_date || item.receivable_due_date.trim() === '');
};

// ============================================
// FINANCIAL TYPES
// ============================================

export interface DisbursementItem {
    amount: number;
    date: string;
}

export interface InstallmentItem {
    number: number;
    value: number;
    dueDate: string;
    format: string;
    person_id?: string;
    business_reference?: string;
    document_id?: string;
    document_title?: string;
    receivable_installment?: string;
    debtor_name?: string;
    debtor_cpf_cnpj?: string;
    debtor_address_street?: string;
    debtor_address_number?: string;
    debtor_address_complement?: string;
    debtor_address_district?: string;
    debtor_address_city?: string;
    debtor_address_state?: string;
    debtor_address_zip_code?: string;
    debtor_address_ibge_code?: string;
}

export interface FeeItem {
    name: string;
    rate: number;
    amount: number;
    form: string;
    interest_period?: string;
}

export interface FeesData {
    items: FeeItem[];
    quantity: number;
}

export interface FiduciaryAlienation {
    name: string;
    value: number;
    description?: string;
    guarantee_type: 'AF' | 'PENHOR';
}

// ============================================
// COLLATERAL TYPES
// ============================================

export const DraweeDocumentType = {
    CPF: 'CPF',
    CNPJ: 'CNPJ',
} as const satisfies Record<string, string>;

export type DraweeDocumentType = typeof DraweeDocumentType[keyof typeof DraweeDocumentType];

/**
 * Returns true if any contract guarantee item is missing required data.
 * Todos os campos do contrato de garantia são obrigatórios.
 */
export const hasContractsWithMissingDrawee = (items: ContractGuaranteeItem[]): boolean => {
    return items.some(item =>
        !item.contract_number?.trim() ||
        !item.contract_value ||
        !item.description?.trim() ||
        !item.file_name?.trim() ||
        !item.drawee_document_type ||
        !item.drawee_document?.trim() ||
        !item.drawee_name?.trim() ||
        !item.drawee_city?.trim() ||
        !item.drawee_state?.trim() ||
        !item.document_title?.trim() ||
        !item.title_installment?.trim() ||
        !item.issue_date?.trim() ||
        !item.operation_date?.trim() ||
        !item.present_value ||
        !item.due_date?.trim() ||
        !item.maturity_value
    );
};

export interface ContractGuaranteeItem {
    collateral_id: string;
    contract_number?: string;
    contract_value: number;
    anticipated_value?: number;
    file_name?: string;
    description?: string;
    // Dados do sacado
    drawee_person_id?: string;
    drawee_document_type?: DraweeDocumentType;
    drawee_document?: string;
    drawee_name?: string;
    drawee_city?: string;
    drawee_state?: string;
    // Dados do título
    document_title?: string;        // Número do título
    title_installment?: string;     // Parcela do título
    issue_date?: string;            // Data de emissão da duplicata
    operation_date?: string;        // Data de operação com a Agrolend
    present_value?: number;         // Valor presente
    due_date?: string;              // Data de vencimento
    maturity_value?: number;        // Valor de vencimento
}

export interface InventoryItem {
    collateral_id: string;
    legal_regime: 'AF' | 'PENHOR';
    asset_type: 'GRAIN' | 'INPUT' | 'PROCESSED' | 'LIVESTOCK';
    name: string;
    value: number;
    product?: string;
    variety?: string;
    quantity?: number;
    unit_of_measure?: 'TON' | 'BAGS' | 'LITERS' | 'HEAD' | 'KG';
    unit_price?: number;
    crop_year?: string;
    storage_location?: string;
    storage_city?: string;
    storage_state?: string;
    warehouse_certificate?: string;
    expiration_date?: string;
    insurance_policy?: string;
    description?: string;
}

export interface MachineryItem {
    collateral_id: string;
    legal_regime: 'AF' | 'PENHOR';
    asset_type: 'HARVESTER' | 'TRACTOR' | 'SPRAYER' | 'PLANTER' | 'TRUCK' | 'OTHER';
    name: string;
    value: number;
    brand?: string;
    model?: string;
    year_manufacture?: number;
    year_model?: number;
    serial_number?: string;
    chassis?: string;
    engine_number?: string;
    plate?: string;
    renavam?: string;
    color?: string;
    hours_of_use?: number;
    condition?: 'NEW' | 'USED' | 'REFURBISHED';
    location_city?: string;
    location_state?: string;
    appraisal_date?: string;
    appraisal_value?: number;
    insurance_policy?: string;
    registration_office?: string;
    registration_number?: string;
    description?: string;
}

export interface RealEstateItem {
    collateral_id: string;
    legal_regime: 'AF' | 'PENHOR';
    property_type: 'RURAL' | 'URBAN' | 'MIXED';
    name: string;
    value: number;
    registration_number?: string;
    registry_office?: string;
    registration_date?: string;
    area_hectares?: number;
    usable_area_hectares?: number;
    nirf?: string;
    ccir?: string;
    car_number?: string;
    address_street?: string;
    address_city?: string;
    address_state?: string;
    address_zip_code?: string;
    coordinates_lat?: number;
    coordinates_lng?: number;
    price_per_hectare?: number;
    appraisal_date?: string;
    appraisal_value?: number;
    encumbrances?: string;
    insurance_policy?: string;
    owner_name?: string;
    owner_document?: string;
    description?: string;
}

export interface SecurityItem {
    collateral_id: string;
    security_type: 'CDBV' | 'LCA' | 'LCI' | 'CRI' | 'CRA';
    initial_value: number;
    interest_rate: number;
    due_date: string;
    issuer?: string;
    description?: string;
}

export interface CollateralAssets {
    inventory?: { items: InventoryItem[]; total: number; quantity: number };
    machinery?: { items: MachineryItem[]; total: number; quantity: number };
    real_estate?: { items: RealEstateItem[]; total: number; quantity: number };
    securities?: { items: SecurityItem[]; total: number; quantity: number };
}

/**
 * Extended CollateralAssets for Structured Operations and Acknowledgement of Debt
 * where receivables and contracts ARE collateral guarantees.
 */
export interface CollateralAssetsWithReceivables extends CollateralAssets {
    receivables?: { items: ReceivableItem[]; total: number; quantity: number; overcollateral?: boolean | null; spreadsheet_processed?: boolean; spreadsheet_items_count?: number; operation_id?: string };
    contracts?: { items: ContractGuaranteeItem[]; total: number; quantity: number };
}

export interface Collaterals {
    total_value: number;
    total_quantity: number;
    assets: CollateralAssets;
}

/**
 * Extended Collaterals for Structured Operations and Acknowledgement of Debt
 * where receivables and contracts ARE collateral guarantees.
 */
export interface CollateralsWithReceivables {
    total_value: number;
    total_quantity: number;
    assets: CollateralAssetsWithReceivables;
}

export const EMPTY_COLLATERALS: Collaterals = {
    total_value: 0,
    total_quantity: 0,
    assets: {
        inventory: { items: [], total: 0, quantity: 0 },
        machinery: { items: [], total: 0, quantity: 0 },
        real_estate: { items: [], total: 0, quantity: 0 },
        securities: { items: [], total: 0, quantity: 0 },
    }
};

export const EMPTY_COLLATERALS_WITH_RECEIVABLES: CollateralsWithReceivables = {
    total_value: 0,
    total_quantity: 0,
    assets: {
        receivables: { items: [], total: 0, quantity: 0, overcollateral: null, spreadsheet_processed: false },
        contracts: { items: [], total: 0, quantity: 0 },
        inventory: { items: [], total: 0, quantity: 0 },
        machinery: { items: [], total: 0, quantity: 0 },
        real_estate: { items: [], total: 0, quantity: 0 },
        securities: { items: [], total: 0, quantity: 0 },
    }
};

// ============================================
// FILE TYPES
// ============================================

export interface SignedContractFile {
    file_id: string;
    file_name: string;
}

export interface ArtifactItem {
    list: string;
    files: string[];
}

// ============================================
// CROP/COMMODITY TYPES
// ============================================

export interface AggregationUnitMeasureDTO {
    plural: string;
    sigla: string;
    singular: string;
}

export interface CropType {
    id: number;
    name: string;
    referency: number;
    description: string;
    qualityStandard: string;
    aggregationUnitMeasureDTO?: AggregationUnitMeasureDTO;
    commodityUnitMeasure?: string;
    cropInformationRegister?: string;
    packagingFormCerc?: string;
    UnitOfMeasurementCerc?: string;
}

// ============================================
// FORM DATA TYPES
// ============================================

export interface BaseCalculation {
    tir_annual: number | null;      // TIR (% a.a)
    tir_monthly: number | null;     // TIR (% a.m)
    spread_annual: number | null;   // Spread (% a.a)
    vpl: number | null;             // VPL (R$)
    vpl_margin: number | null;      // Margem VPL (%)
    duration_days: number | null;   // Duration (Número de Dias)
}

export interface AcknowledgementDebtForm {
    assignor: string;
    recipient: string;
    total_amount: number;
    loan_amount: number;
    expiration_date: string;
    emission_date: string;
    approved_on: string;
    operation_number: string;
    external_link: string;
    rebate_amount: number;
    guarantee_value: number | null;
    status?: string;
    sequencial_number?: number;
    originator: string;
    operation_uf: string;
    operation_city: string;
    original_contract_discharged: number[];
    credit_product_type_name: string;
    receiving_account_name?: string;
    crop_type: {
        name: string;
        id: string;
    };
    collaterals: CollateralsWithReceivables;
    disbursement: {
        totalAmount: number;
        disbursements: DisbursementItem[];
    };
    installments: InstallmentItem[];
    installment_format: string;
    guarantors: GuarantorsData;
    main_risk: {
        uuid: string;
        name: string;
    };
    fees: FeesData;
    artifacts: ArtifactItem[];
    signed_contract_file: SignedContractFile;
    base_calculation?: BaseCalculation;
    billing_config?: BillingConfig;
    assignor_billing_address?: {
        name: string;
        document_number: string;
        address_street: string;
        address_number: string;
        address_complement: string;
        address_neighborhood: string;
        address_city: string;
        address_state: string;
        address_zip_code: string;
    };
}

export interface StructuredOperationForm {
    assignor: string;
    recipient: string;
    total_amount: number;
    loan_amount: number;
    nominal_value: number;
    expiration_date: string;
    emission_date: string;
    approved_on: string;
    operation_number: string;
    external_link: string;
    rebate_amount: number;
    guarantee_value: number | null;
    with_recourse?: boolean;
    status?: string;
    sequencial_number?: number;
    originator: string;
    operation_uf: string;
    operation_city: string;
    credit_product_type_name: string;
    receiving_account_name?: string;
    crop_type?: {
        name: string;
        id: string;
    };
    description_product?: {
        name: string;
        id: string;
    };
    collaterals: CollateralsWithReceivables;
    disbursement: {
        totalAmount: number;
        disbursements: DisbursementItem[];
    };
    installments: InstallmentItem[];
    installment_format: string;
    guarantors: GuarantorsData;
    main_risk: {
        uuid: string;
        name: string;
    };
    fees: FeesData;
    artifacts: ArtifactItem[];
    signed_contract_file: SignedContractFile;
    base_calculation?: BaseCalculation;
    billing_config?: BillingConfig;
    assignor_billing_address?: {
        name: string;
        document_number: string;
        address_street: string;
        address_number: string;
        address_complement: string;
        address_neighborhood: string;
        address_city: string;
        address_state: string;
        address_zip_code: string;
    };
}

export interface CPRFFormData {
    type: string;
    operation_number: string;
    sequencial_number: number;
    nominal_value: number;
    loan_amount?: number;
    approved_value: string;
    interest_rate: string;
    expiration_date: string;
    emission_date: string;
    approved_on: string;
    external_link?: string;
    credit_product_type_name?: string;
    receiving_account_name?: string;
    operation_uf: string;
    operation_city: string;
    operation_dictionary: {
        type: string;
        nominal_value: number;
        requested_value: number;
        interest_rate: number;
        emission_date: string;
        status: number;
        guarantee_value?: number | null;
        measures: {
            referency: number;
            aggregation_unit_measure: string;
            amount_in_aggregation_unit: number;
            unit_measure: string;
        };
        commodity: {
            harvest_year_reference: string;
            price: number;
            quality?: string;
            description?: string;
            amount?: number;
            quotation: {
                date: string;
                source: string;
                city: string;
                province_name: string;
                province_initials: string;
            };
        };
        crop_type: {
            id: number;
            name: string;
        };
        main_risk?: {
            uuid: string;
            name: string;
        };
        guarantors?: Array<{
            uuid: string;
            name: string;
            document?: string;
            entity_type?: string;
            guarantee_percentage: number;
            participant_type_name?: string;
        }>;
        installment?: {
            total: number;
            lastDueDate: string;
            quantity: number;
            items?: Array<{
                number: number;
                value: number;
                dueDate: string;
                format: string;
            }>;
        };
        fees?: {
            quantity: number;
            items: Array<{
                name: string;
                rate: number;
                amount: number;
                form: string;
                interest_period?: string;
            }>;
        };
    };
    base_calculation?: BaseCalculation;
    assignor_billing_address?: {
        name: string;
        document_number: string;
        address_street: string;
        address_number: string;
        address_complement: string;
        address_neighborhood: string;
        address_city: string;
        address_state: string;
        address_zip_code: string;
    };
}

// ============================================
// OPTION TYPES
// ============================================

export interface OptionProps {
    value: string;
    label: string;
}

export interface ProvinceOption extends OptionProps {
    initials: string;
}

// ============================================
// CONTRACT TYPES
// ============================================

export type ContractStatus =
    | 'Gerado'
    | 'Aguardando partes'
    | 'Em andamento'
    | 'Suspenso'
    | 'Cancelado'
    | 'Finalizado com sucesso'
    | 'Expirado';

export type OperationType =
    | 'RECEIVABLES_ASSIGNMENT'
    | 'STRUCTURED_OPERATION'
    | 'ACKNOWLEDGEMENT_OF_DEBT'
    | 'LOAN'
    | 'CPRF'
    | 'DRAWEE_RISK';

export type ParticipantType =
    | 'BORROWER'
    | 'GUARANTOR'
    | 'ASSIGNOR'
    | 'GUARANTOR-RETAIL'
    | 'RECIPIENT';
